import React from 'react';
import styled, { css } from 'styled-components';
import type { StudioSidePanelContextProps } from 'venn-components';
import { SidePanelSettings } from 'venn-components';
import { Headline3, GetColor, Icon } from 'venn-ui-kit';
import type { AnalysisView } from 'venn-api';
import LinkableSetupSectionGroup from './tabs/sections/section-groups/LinkableSetupSectionGroup';
import GlobalLinkedInfo from './tabs/sections/components/GlobalLinkedInfo';
import { buttonize, withSuspense } from 'venn-utils';
import { useRecoilValue } from 'recoil';
import { isReportState, rightPanelIsLinked, selectedBlockIdState, selectedBlockSettings } from 'venn-state';
import BlockTabs from './tabs/BlockTabs';

// Set min height to make sure investment/portfolio search box can scroll up
const CONTENT_MIN_HEIGHT = '700px';

type StudioV1PanelProps = {
  openStudioSetup: () => void;
  globalView?: AnalysisView;
} & Pick<StudioSidePanelContextProps, 'onSelectBlock'>;

/**
 * @deprecated this panel will be replaced by {@link StudioBlockConfigPanelContent} in Input Management V2.
 * Any changes made to this code should be considered for the StudioBlockConfigPanelContent code as well.
 */
const StudioRightPanelContent = ({ globalView, onSelectBlock, openStudioSetup }: StudioV1PanelProps) => {
  const isReport = useRecoilValue(isReportState);
  const selected = useRecoilValue(selectedBlockIdState);
  const blockSetting = useRecoilValue(selectedBlockSettings);
  const linked = useRecoilValue(rightPanelIsLinked);
  const isGlobal = !selected;

  const items = [
    {
      key: 'settings',
      text: 'Settings',
      labelComponent:
        !isGlobal && blockSetting?.customBlockType !== 'MARKDOWN' ? (
          <>
            Setup <SetupLinkIcon type={linked ? 'link' : 'unlink'} />
          </>
        ) : undefined,
      Component: !selected ? (
        <>
          <GlobalLinkedInfo globalView={globalView} onSelectBlock={onSelectBlock} />
          <LinkableSetupSectionGroup hideAllocatorLauncher={isReport} />
        </>
      ) : (
        <BlockTabs selected={selected} />
      ),
    },
  ];

  const headerComponent = isGlobal ? (
    <>
      <BlockLabel>{isReport ? 'Report Setup' : 'Studio Setup'}</BlockLabel>
      <Headline3 style={{ marginLeft: '10px' }}>Global Configuration</Headline3>
    </>
  ) : (
    <>
      <BlockLabel {...buttonize(openStudioSetup)} isLink>
        Global Configuration
      </BlockLabel>
      <BlockLabel>Block Configuration</BlockLabel>
      <Headline3 style={{ marginLeft: '10px' }}>{blockSetting?.title}</Headline3>
    </>
  );

  return (
    <SidePanelSettings items={items} customHeaderComponent={headerComponent} contentMinHeight={CONTENT_MIN_HEIGHT} />
  );
};

export default withSuspense(null, StudioRightPanelContent);

const BlockLabel = styled.div<{ isLink?: boolean }>`
  font-size: 10px;
  color: ${GetColor.GreyScale.Grey80};
  background-color: ${GetColor.GreyScale.Grey10};
  border-radius: 4px;
  padding: 2px 5px;
  display: inline-block;
  margin-top: 10px;
  ${({ isLink }) =>
    isLink &&
    css`
      cursor: pointer;
      color: ${GetColor.White};
      background-color: ${GetColor.Primary.Dark};
      :hover {
        background-color: ${GetColor.Primary.Main};
      }
    `}
`;

const SetupLinkIcon = styled(Icon)`
  padding-left: 3px;
`;
